import { useState } from 'react';

import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentsIcon from '@mui/icons-material/Payments';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Undo from '@mui/icons-material/Undo';
import { useCheckPermission } from 'hooks-ts/usePermission';
import { type Invoice, INVOICE_PAYMENT, INVOICE_STATUS, type InvoiceWithBatch } from '@a1/shared';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { invoicesV4FeatureFlagEnabled } from 'constants-ts/featureFlags';

interface ActionsColumnProps {
  invoice: Invoice | InvoiceWithBatch;
  setSelectedInvoice: React.Dispatch<React.SetStateAction<Invoice | InvoiceWithBatch | null>>;
  setOpenInvoicePaymentsDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSendInvoicePdfDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenMarkAsPaidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenVoidDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActionsColumn = ({
  invoice,
  setSelectedInvoice,
  setOpenInvoicePaymentsDialog,
  setOpenInvoicePdfDialog,
  setOpenSendInvoicePdfDialog,
  setOpenMarkAsPaidDialog,
  setOpenVoidDialog
}: ActionsColumnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const permissionCheck = useCheckPermission();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <IconButton
        id={`open-action-menu-button-${invoice.id}`}
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="open action menu"
        onClick={handleOpenActionMenu}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`action-menu-${invoice.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseActionMenu}
        slotProps={{
          list: {
            'aria-labelledby': `open-action-menu-button-${invoice.id}`
          }
        }}
      >
        {setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canViewPaymentHistory()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to view invoice payments.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <PaymentsIcon fontSize="small" />
            </ListItemIcon>
            View Invoice Payments
          </MenuItem>
        )}

        {invoice.isLinkedToCreditNote && invoicesV4FeatureFlagEnabled && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/manage-invoices/${invoice.id}/credit-notes`);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            View Credit Notes
          </MenuItem>
        )}

        {setOpenInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canExportSendInvoices()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to view invoice PDFs.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            View Invoice PDF
          </MenuItem>
        )}

        {setOpenSendInvoicePdfDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canExportSendInvoices()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to email invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenSendInvoicePdfDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <AttachEmailIcon fontSize="small" />
            </ListItemIcon>
            Email Invoice
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to mark invoices as paid.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenMarkAsPaidDialog?.(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <CheckCircle fontSize="small" />
            </ListItemIcon>
            Mark as Paid
          </MenuItem>
        )}

        {invoice.status === INVOICE_STATUS.PAID.id && setOpenInvoicePaymentsDialog && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus() || !permissionCheck.canRefundPayment()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to refund invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenInvoicePaymentsDialog(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <Undo fontSize="small" />
            </ListItemIcon>
            Refund
          </MenuItem>
        )}

        {(invoice.status === INVOICE_STATUS.AWAITING_PAYMENT.id ||
          (invoice.status === INVOICE_STATUS.PAID.id && invoice.paymentOption !== INVOICE_PAYMENT.ONLINE)) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              if (!permissionCheck.canChangeInvoiceStatus()) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to void invoices.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setSelectedInvoice(invoice);
              setOpenVoidDialog?.(true);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            Void
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default ActionsColumn;
